/**
 * A private foundation's Form 990-PF worksheet, a tab on Reports, and the
 * CPA's markings behind it (migration 0085; N8 of the #55 plan, §B.18). The
 * figures are the API's (apps/api/src/services/foundation.ts); this lays them
 * out. **A prompt for the CPA, never a filing.**
 *
 * THE YEAR IS THE FISCAL YEAR (round 118's PF-1), the one the Reports period
 * ends in, resolved by the API from that date. The heading states its first
 * and last day, so a foundation whose year starts in July reads which twelve
 * months it covers.
 *
 * Every money cell is signed: an excess of expenses is negative, and a
 * substring match on "$12.00" cannot tell "-$12.00" from it.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, type DisqualifiedRelationship, type Pf990Worksheet, type PfMarking, type PfMarkings } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

export const RELATIONSHIP_LABELS: Record<DisqualifiedRelationship, string> = {
  founder: 'Founder',
  substantial_contributor: 'Substantial Contributor',
  foundation_manager: 'Foundation Manager',
  family_member: 'Family Member',
  controlled_entity: 'Controlled Entity',
};

/** "Not Available" where the cash-basis conversion refused, so a blank never reads as zero. */
function cents(value: number | null): string {
  return value === null ? 'Not Available' : formatCents(value);
}

export function Form990PfReport({
  worksheet,
  canMark,
  onChanged,
}: {
  worksheet: Pf990Worksheet;
  /** manage_business (0069): who enters the CPA's markings. */
  canMark: boolean;
  /** Called after the markings change, so the worksheet is read again. */
  onChanged: () => void;
}) {
  const [marking, setMarking] = useState(false);
  const { year, from, to, totals } = worksheet;
  const revenue = worksheet.rows.filter((row) => row.type === 'revenue');
  const expenses = worksheet.rows.filter((row) => row.type === 'expense');
  const qd = worksheet.qualifyingDistributions;

  const partIRow = (row: Pf990Worksheet['rows'][number]) => (
    <tr key={row.accountId}>
      <td>
        {row.code} {row.name}
        {!row.marked && <span className="muted"> · Not Marked</span>}
      </td>
      <td className="num">{formatCents(row.perBooksCents)}</td>
      <td className="num">{formatCents(row.netInvestmentCents)}</td>
      <td className="num">{formatCents(row.adjustedNetCents)}</td>
      <td className="num">{row.type === 'revenue' ? '' : cents(row.charitableCents)}</td>
    </tr>
  );

  return (
    <div className="report form-990-pf">
      <h2>Form 990-PF</h2>
      <p className="muted">
        Fiscal year {year}: {from} to {to}. A worksheet for your CPA, never a filing.{' '}
        <InfoNote label="What This Worksheet Is">
          Part I&apos;s four columns for the foundation&apos;s fiscal year. Column (a) is the books. Columns (b) and (c) are each
          account&apos;s share of (a), and column (d) its share of what was paid, on the cash basis; the shares are your CPA&apos;s, entered
          under Markings for each year. An account with a line and no marking is listed below, so a missing marking is seen rather than read
          as zero. Every figure is a prompt for your CPA.
        </InfoNote>
      </p>
      {canMark && (
        <p>
          <button type="button" onClick={() => setMarking((open) => !open)}>
            {marking ? 'Close Markings' : `Markings For ${year}`}
          </button>
        </p>
      )}
      {marking && <MarkingsEditor year={year} operating={worksheet.operating} onSaved={onChanged} />}

      {worksheet.cashBasisRefusal !== null && (
        <p role="alert" className="error">
          Column (d) cannot be worked out for this year: {worksheet.cashBasisRefusal}
        </p>
      )}

      <div className="table-scroll">
        <table className="lines" aria-label="Part I">
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">(a) Per Books</th>
              <th className="num">(b) Net Investment Income</th>
              <th className="num">(c) Adjusted Net Income</th>
              <th className="num">(d) Charitable Disbursements</th>
            </tr>
          </thead>
          <tbody>
            <tr className="report-section">
              <th colSpan={5}>Revenue</th>
            </tr>
            {revenue.map(partIRow)}
            <tr className="report-total">
              <td>Total Revenue</td>
              <td className="num">{formatCents(totals.revenueCents)}</td>
              <td className="num">{formatCents(revenue.reduce((sum, row) => sum + row.netInvestmentCents, 0))}</td>
              <td className="num">{formatCents(revenue.reduce((sum, row) => sum + row.adjustedNetCents, 0))}</td>
              <td />
            </tr>
            <tr className="report-section">
              <th colSpan={5}>Expenses</th>
            </tr>
            {expenses.map(partIRow)}
            <tr className="report-total">
              <td>Total Expenses</td>
              <td className="num">{formatCents(totals.expenseCents)}</td>
              <td className="num">{formatCents(expenses.reduce((sum, row) => sum + row.netInvestmentCents, 0))}</td>
              <td className="num">{formatCents(expenses.reduce((sum, row) => sum + row.adjustedNetCents, 0))}</td>
              <td className="num">{cents(totals.charitableCents)}</td>
            </tr>
            <tr className="report-total">
              <td>Excess Of Revenue Over Expenses</td>
              <td className="num">{formatCents(totals.excessCents)}</td>
              <td colSpan={3} />
            </tr>
            <tr className="report-total">
              <td>Net Investment Income</td>
              <td />
              <td className="num">{formatCents(totals.netInvestmentIncomeCents)}</td>
              <td colSpan={2} />
            </tr>
            <tr className="report-total">
              <td>Adjusted Net Income</td>
              <td colSpan={2} />
              <td className="num">{formatCents(totals.adjustedNetIncomeCents)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Qualifying Distributions</h3>
      <table className="lines pf-summary" aria-label="Qualifying Distributions">
        <tbody>
          <tr>
            <td>Charitable Disbursements, Column (d)</td>
            <td className="num">{cents(qd.charitableCents)}</td>
          </tr>
          <tr>
            <td>Exempt-Use Asset Purchases</td>
            <td className="num">{formatCents(qd.assetPurchasesCents)}</td>
          </tr>
          <tr className="report-total">
            <td>Total</td>
            <td className="num">{cents(qd.totalCents)}</td>
          </tr>
        </tbody>
      </table>
      {qd.purchases.length > 0 && (
        <EntryTable
          label="Exempt-Use Asset Purchases"
          entries={qd.purchases.map((purchase) => ({
            ...purchase,
            cells: [formatCents(purchase.amountCents)],
          }))}
          columns={['Amount']}
        />
      )}

      <h3>
        Recoveries, For The CPA{' '}
        <InfoNote label="Why A Sale Is Not Netted">
          A purchase of an asset used in exempt activities counts in full in the year it was paid for. Selling the asset later does not undo
          that distribution: what was received is a recovery, added to the year&apos;s distributable amount on a line of its own. So each
          sale is listed here at what the bank received, which is what the return asks for, beside the cost the books wrote off.
        </InfoNote>
      </h3>
      {worksheet.recoveries.length === 0 ? (
        <p className="muted">No asset marked for exempt use was sold or written off in the year.</p>
      ) : (
        <EntryTable
          label="Recoveries"
          entries={worksheet.recoveries.map((recovery) => ({
            ...recovery,
            cells: [formatCents(recovery.bookCreditCents), formatCents(recovery.proceedsCents)],
          }))}
          columns={['Book Credit', 'Proceeds']}
        />
      )}

      {worksheet.directConduct !== null && (
        <>
          <h3>Direct Conduct</h3>
          <p className="muted">
            The test uses the lesser of adjusted net income and the minimum investment return; the minimum investment return needs
            investment values the books do not hold, so this compares against adjusted net income alone.
          </p>
          <table className="lines pf-summary" aria-label="Direct Conduct">
            <tbody>
              <tr>
                <td>Spent Directly On The Foundation&apos;s Own Activities</td>
                <td className="num">{cents(worksheet.directConduct.expenseCents)}</td>
              </tr>
              <tr>
                <td>Exempt-Use Asset Purchases</td>
                <td className="num">{formatCents(worksheet.directConduct.assetPurchasesCents)}</td>
              </tr>
              <tr className="report-total">
                <td>Total Direct Conduct</td>
                <td className="num">{cents(worksheet.directConduct.totalCents)}</td>
              </tr>
              <tr>
                <td>85% Of Adjusted Net Income</td>
                <td className="num">{formatCents(worksheet.directConduct.eightyFivePercentCents)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <h3>
        Excise Tax Estimate{' '}
        <InfoNote label="About The Rate">
          Net investment income at {(worksheet.excise.rateBp / 100).toFixed(2)}
          %, the rate believed to apply, for your CPA to confirm. It is zero when net investment income is not positive. An exempt operating
          foundation may owe none, which is your CPA&apos;s to decide.
        </InfoNote>
      </h3>
      <table className="lines pf-summary" aria-label="Excise Tax Estimate">
        <tbody>
          <tr>
            <td>Net Investment Income</td>
            <td className="num">{formatCents(worksheet.excise.netInvestmentIncomeCents)}</td>
          </tr>
          <tr className="report-total">
            <td>Estimate At {(worksheet.excise.rateBp / 100).toFixed(2)}%</td>
            <td className="num">{formatCents(worksheet.excise.estimateCents)}</td>
          </tr>
        </tbody>
      </table>

      <h3>Payments To Disqualified Persons</h3>
      {worksheet.disqualifiedPayments.length === 0 ? (
        <p className="muted">No posted entry in the year names a disqualified person.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines" aria-label="Payments To Disqualified Persons">
            <thead>
              <tr>
                <th>Date</th>
                <th>Party</th>
                <th>Relationship</th>
                <th>Memo</th>
                <th className="num">Paid</th>
              </tr>
            </thead>
            <tbody>
              {worksheet.disqualifiedPayments.map((payment) => (
                <tr key={payment.headerId}>
                  <td>
                    <Link to={`/journals/${payment.headerId}`}>{payment.date}</Link>
                  </td>
                  <td>{payment.partyName}</td>
                  <td>{RELATIONSHIP_LABELS[payment.relationship]}</td>
                  <td>{payment.memo ?? ''}</td>
                  <td className="num">{formatCents(payment.paidCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Unmarked Accounts</h3>
      {worksheet.unmarked.length === 0 ? (
        <p className="muted">Every account with a line in the year is marked.</p>
      ) : (
        <ul aria-label="Unmarked Accounts">
          {worksheet.unmarked.map((account) => (
            <li key={account.accountId}>
              {account.code} {account.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EntryTable({
  label,
  entries,
  columns,
}: {
  label: string;
  entries: Array<{
    headerId: string;
    date: string;
    accountCode: string;
    accountName: string;
    memo: string | null;
    cells: string[];
  }>;
  columns: string[];
}) {
  return (
    <div className="table-scroll">
      <table className="lines" aria-label={label}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Account</th>
            <th>Memo</th>
            {columns.map((column) => (
              <th key={column} className="num">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={`${entry.headerId}-${entry.accountCode}`}>
              <td>
                <Link to={`/journals/${entry.headerId}`}>{entry.date}</Link>
              </td>
              <td>
                {entry.accountCode} {entry.accountName}
              </td>
              <td>{entry.memo ?? ''}</td>
              {entry.cells.map((cell, index) => (
                <td key={columns[index]} className="num">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The markings
// ---------------------------------------------------------------------------

type ShareKey = 'netInvestmentBp' | 'adjustedNetBp' | 'charitableBp' | 'directConductBp';

/** Basis points as a percent a person types: 5000 is "50", 3333 is "33.33". */
export function bpToPercent(bp: number | null): string {
  if (bp === null) return '';
  const fraction = bp % 100;
  return fraction === 0 ? String(bp / 100) : `${Math.floor(bp / 100)}.${String(fraction).padStart(2, '0').replace(/0$/, '')}`;
}

/** A typed percent as basis points: blank is no share, and anything not 0 to 100 with two places is null. */
export function percentToBp(text: string): number | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const match = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) return 'invalid';
  const bp = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return bp > 10_000 ? 'invalid' : bp;
}

interface Draft {
  accountId: string;
  shares: Record<ShareKey, string>;
  exemptUse: boolean;
}

function toDraft(account: PfMarkings['accounts'][number]): Draft {
  const marking = account.marking;
  return {
    accountId: account.accountId,
    shares: {
      netInvestmentBp: bpToPercent(marking?.netInvestmentBp ?? null),
      adjustedNetBp: bpToPercent(marking?.adjustedNetBp ?? null),
      charitableBp: bpToPercent(marking?.charitableBp ?? null),
      directConductBp: bpToPercent(marking?.directConductBp ?? null),
    },
    exemptUse: marking?.exemptUse ?? false,
  };
}

const SHARE_COLUMNS: Array<{
  key: ShareKey;
  label: string;
  types: Array<'revenue' | 'expense'>;
}> = [
  {
    key: 'netInvestmentBp',
    label: '(b) Net Investment %',
    types: ['revenue', 'expense'],
  },
  {
    key: 'adjustedNetBp',
    label: '(c) Adjusted Net %',
    types: ['revenue', 'expense'],
  },
  { key: 'charitableBp', label: '(d) Charitable %', types: ['expense'] },
  { key: 'directConductBp', label: 'Direct Conduct %', types: ['expense'] },
];

export function MarkingsEditor({ year, operating, onSaved }: { year: number; operating: boolean; onSaved: () => void }) {
  const [markings, setMarkings] = useState<PfMarkings | null>(null);
  const [drafts, setDrafts] = useState<Map<string, Draft>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const columns = SHARE_COLUMNS.filter((column) => operating || column.key !== 'directConductBp');

  const show = (next: PfMarkings) => {
    setMarkings(next);
    setDrafts(new Map(next.accounts.map((account) => [account.accountId, toDraft(account)])));
  };
  useEffect(() => {
    let current = true;
    api
      .pfMarkings(year)
      .then((next) => current && show(next))
      .catch((caught: unknown) => current && setError(caught instanceof ApiError ? caught.message : 'Could not read the markings.'));
    return () => {
      current = false;
    };
  }, [year]);

  const edit = (accountId: string, change: (draft: Draft) => Draft) =>
    setDrafts((current) => {
      const next = new Map(current);
      next.set(accountId, change(current.get(accountId)!));
      return next;
    });

  const save = () => {
    if (!markings) return;
    const body: PfMarking[] = [];
    for (const account of markings.accounts) {
      const draft = drafts.get(account.accountId)!;
      const shares: Record<ShareKey, number | null> = {
        netInvestmentBp: null,
        adjustedNetBp: null,
        charitableBp: null,
        directConductBp: null,
      };
      for (const column of SHARE_COLUMNS) {
        const parsed = percentToBp(draft.shares[column.key]);
        if (parsed === 'invalid') {
          setStatus(null);
          setError(`${account.code} ${account.name}: a share is a percent from 0 to 100, with at most two decimal places.`);
          return;
        }
        shares[column.key] = parsed;
      }
      body.push({
        accountId: account.accountId,
        ...shares,
        exemptUse: draft.exemptUse,
      });
    }
    setBusy(true);
    api
      .savePfMarkings(year, body)
      .then((next) => {
        show(next);
        setError(null);
        setStatus(`Markings for ${year} saved.`);
        onSaved();
      })
      .catch((caught: unknown) => {
        setStatus(null);
        setError(caught instanceof ApiError ? caught.message : 'Could not save the markings.');
      })
      .finally(() => setBusy(false));
  };

  const copy = () => {
    if (!markings) return;
    if (markings.previousYearMarkings === 0) {
      setError(null);
      setStatus(`${year - 1} has no markings to copy.`);
      return;
    }
    setBusy(true);
    api
      .copyPfMarkings(year)
      .then(async ({ copied }) => {
        show(await api.pfMarkings(year));
        setError(null);
        setStatus(
          copied === 0
            ? `Every account marked in ${year - 1} is already marked for ${year}; nothing was copied.`
            : `${copied} ${copied === 1 ? 'marking' : 'markings'} copied from ${year - 1}.`,
        );
        onSaved();
      })
      .catch((caught: unknown) => {
        setStatus(null);
        setError(caught instanceof ApiError ? caught.message : 'Could not copy the markings.');
      })
      .finally(() => setBusy(false));
  };

  return (
    <section className="pf-markings" aria-label={`Markings For ${year}`}>
      <h3>
        Markings For {year}{' '}
        <InfoNote label="What A Marking Is">
          Your CPA&apos;s share of each account in each column, as a percent, for this fiscal year only: a change here never rewrites
          another year&apos;s worksheet. A blank is no share; 0 is a share of nothing, and marks the account. An asset marked Exempt Use is
          used directly in the foundation&apos;s exempt activities, and its purchases count as qualifying distributions. Copy From{' '}
          {year - 1} fills only the accounts not yet marked for {year}.
        </InfoNote>
      </h3>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {status && <p role="status">{status}</p>}
      {markings && (
        <>
          <div className="table-scroll">
            <table className="lines" aria-label={`Markings For ${year}`}>
              <thead>
                <tr>
                  <th>Account</th>
                  {columns.map((column) => (
                    <th key={column.key} className="num">
                      {column.label}
                    </th>
                  ))}
                  <th>Exempt Use</th>
                </tr>
              </thead>
              <tbody>
                {markings.accounts.map((account) => {
                  const draft = drafts.get(account.accountId)!;
                  const name = `${account.code} ${account.name}`;
                  return (
                    <tr key={account.accountId}>
                      <td>
                        {name}
                        {!account.isActive && <span className="muted"> · Inactive</span>}
                      </td>
                      {columns.map((column) => (
                        <td key={column.key} className="num">
                          {account.type !== 'asset' && column.types.includes(account.type) && (
                            <input
                              className="pf-share"
                              inputMode="decimal"
                              aria-label={`${column.label} for ${name}`}
                              value={draft.shares[column.key]}
                              onChange={(event) => {
                                const value = event.target.value;
                                edit(account.accountId, (current) => ({
                                  ...current,
                                  shares: {
                                    ...current.shares,
                                    [column.key]: value,
                                  },
                                }));
                              }}
                            />
                          )}
                        </td>
                      ))}
                      <td>
                        {account.type === 'asset' && (
                          <input
                            type="checkbox"
                            aria-label={`Exempt Use for ${name}`}
                            checked={draft.exemptUse}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              edit(account.accountId, (current) => ({
                                ...current,
                                exemptUse: checked,
                              }));
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="actions">
            <button type="button" onClick={save} disabled={busy}>
              Save Markings
            </button>{' '}
            <button type="button" onClick={copy} disabled={busy}>
              Copy From {year - 1}
            </button>
          </p>
        </>
      )}
    </section>
  );
}
